import React, { FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { CheckCircleIcon, SendDocumentIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { useSelectedCompany } from '../../../../../hooks/index';
import { PageRoute } from '../../../../../types/page.types';
import { TemplateFormStore } from '../../../../documents/templates/store/template-form.store';
import { EventsModalType } from '../../../enums/events-modal-type.const';
import { useTemplatesForPlan } from '../../../hooks/use-templates-for-plan.hook';
import { eventsModalStore } from '../../../store/events-modal.store';
import { EquityPlanCombobox } from '../../components/index';
import { ChildrenFormProps } from '../../event-form.types';
import { GrantSignatureOption } from '../../event-form.validation';

const ErrorWrapper: FC<{
  actionButton?: ReactNode;
  children: ReactNode;
  errorMessages: ReactNode[];
  hasError: boolean;
}> = ({ actionButton, children, errorMessages, hasError }) => {
  if (!hasError) return null;
  return (
    <div className="flex flex-col overflow-hidden rounded-lg bg-fireside-50 p-2 transition-all">
      <div className="pointer-events-none rounded-[4px] bg-white opacity-60">{children}</div>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <div className="flex flex-col gap-0.5">
          {errorMessages.map((msg, index) => (
            <span className="text-xs text-fireside-700" key={index}>
              {msg}
            </span>
          ))}
        </div>
        {actionButton && (
          <div className="shrink-0 text-xs font-medium text-brand-700 underline">
            {actionButton}
          </div>
        )}
      </div>
    </div>
  );
};

const OptionCard: FC<{
  badge?: ReactNode;
  description: ReactNode;
  icon: ReactNode;
  isSelected: boolean;
  onClick: () => void;
  title: ReactNode;
}> = ({ badge, description, icon, isSelected, onClick, title }) => (
  <button
    className={twMerge(
      'flex flex-col gap-2 rounded-[4px] border px-4 py-3 text-left transition-colors',
      'border-gray-200 bg-white hover:bg-gray-25',
      isSelected && 'border-brand-500 bg-brand-25',
    )}
    onClick={onClick}
    type="button"
  >
    <div className="flex w-full items-center justify-between">
      {icon}
      {badge}
    </div>
    <div className="flex flex-col gap-1">
      <span className={twMerge('text-sm font-[550] text-gray-700', isSelected && 'text-brand-700')}>
        {title}
      </span>
      <span className={twMerge('text-xs', isSelected ? 'text-brand-600' : 'text-gray-500')}>
        {description}
      </span>
    </div>
  </button>
);

const GrantStepFirst: FC<ChildrenFormProps> = ({
  companyId,
  control,
  editWizardStep,
  event,
  formData,
  handleCloseModal,
  isGrantEditWizard,
  nextFormStep,
  prevFormStep,
  setFormData,
}) => {
  const navigate = useNavigate();
  const { selectedCompany } = useSelectedCompany();
  const remainingEnvelopes = selectedCompany?.docusignEnvelope?.remaining ?? 0;

  const sharePlan = useWatch({
    control,
    name: 'stepOne.eventDetails.grant.sharePlan',
    defaultValue: undefined,
  });
  const equityPlanId = sharePlan?.id ?? '';
  const { field: signatureField } = useController({
    control,
    name: 'stepOne.eventDetails.grant.signatureOption',
  });

  const { hasTemplatesForPlan, isPending } = useTemplatesForPlan(companyId, equityPlanId);
  const docuSignHasIssue = remainingEnvelopes === 0 || !hasTemplatesForPlan;

  const grantPlanSyncRef = useRef<{ eventKey: string; planId: string } | null>(null);

  useEffect(() => {
    const planId = equityPlanId?.trim() ?? '';
    const eventKey = event?.id ?? 'new';
    const sync = grantPlanSyncRef.current;

    if (!sync || sync.eventKey !== eventKey) {
      grantPlanSyncRef.current = { eventKey, planId };
      return;
    }

    if (sync.planId !== planId) {
      grantPlanSyncRef.current = { eventKey, planId };
      setFormData('stepOne.eventDetails.grant.selectedTemplateId', undefined);
      setFormData('stepOne.eventDetails.grant.signatoryAssignments', {});

      const selected = sharePlan;
      if (selected?.id?.trim()) {
        const syncedSharePlan = {
          id: selected.id,
          name: selected.name ?? '',
          pool: { id: selected.pool?.id ?? '' },
        };
        const items = formData('stepOne.eventDetails.grant.grantItems');
        const len = items?.length ?? 0;

        for (let i = 0; i < len; i++) {
          setFormData(`stepOne.eventDetails.grant.grantItems.${i}.sharePlan`, syncedSharePlan);
          setFormData(`stepOne.eventDetails.grant.grantItems.${i}.initialShares`, undefined);
          setFormData(`stepOne.eventDetails.grant.grantItems.${i}.initialSharePlanId`, undefined);
          setFormData(`stepOne.eventDetails.grant.grantItems.${i}.balance`, 0);
        }
      }
    }
  }, [equityPlanId, event?.id, formData, setFormData, sharePlan]);

  useEffect(() => {
    if (!docuSignHasIssue) return;
    if (signatureField.value === GrantSignatureOption.SIGNATURE) {
      setFormData(
        'stepOne.eventDetails.grant.signatureOption',
        GrantSignatureOption.WITHOUT_SIGNATURE,
      );
    }
  }, [docuSignHasIssue, setFormData, signatureField.value]);

  const hasPlan = Boolean(equityPlanId?.trim());
  const canProceed = hasPlan && signatureField.value != null && !isPending;

  const goToTemplatesAndOpenCreate = () => {
    TemplateFormStore.getState().toggleAddState(true);
    handleCloseModal();
    navigate(PageRoute.DOCUMENTS_TEMPLATES);
  };

  const errorMessages = useMemo((): ReactNode[] => {
    if (remainingEnvelopes === 0) {
      return [<AppFormattedMessage id="NO_ENVELOPES_AVAILABLE_DOCUSIGN" key="envelopes" />];
    }
    if (!hasTemplatesForPlan) {
      return [<AppFormattedMessage id="NO_TEMPLATE_AVAILABLE_FOR_PLAN" key="templates" />];
    }
    return [];
  }, [hasTemplatesForPlan, remainingEnvelopes]);

  const docuSignOptionCard = (
    <OptionCard
      badge={
        !docuSignHasIssue ? (
          <span className="rounded-2xl bg-forest-50 px-2 text-xs text-forest-600">
            {remainingEnvelopes} <AppFormattedMessage id="REMAINING" />{' '}
            <AppFormattedMessage id="ENVELOPES" />
          </span>
        ) : undefined
      }
      description={<AppFormattedMessage id="GRANT_DOCUSIGN_DESCRIPTION" />}
      icon={
        <SendDocumentIcon
          className="size-6 -rotate-90"
          iconColor={
            signatureField.value === GrantSignatureOption.SIGNATURE ? '#2565C8' : '#344054'
          }
        />
      }
      isSelected={signatureField.value === GrantSignatureOption.SIGNATURE}
      onClick={() =>
        setFormData('stepOne.eventDetails.grant.signatureOption', GrantSignatureOption.SIGNATURE)
      }
      title={<AppFormattedMessage id="SEND_FOR_SIGNATURE_DOCUSIGN" />}
    />
  );

  const handleAddWithoutSignature = () => {
    setFormData(
      'stepOne.eventDetails.grant.signatureOption',
      GrantSignatureOption.WITHOUT_SIGNATURE,
    );
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <section className="flex flex-col">
          <div className="flex flex-col gap-2 rounded-lg border border-gray-100 p-4 shadow-sm">
            <label className="text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="EQUITY_PLAN" />
            </label>
            <EquityPlanCombobox companyId={companyId} control={control} />
          </div>
          <p className="px-4 py-3 text-xs text-gray-500">
            <AppFormattedMessage id="EQUITY_PLAN_SIGNATURE_HELPER" />
          </p>
        </section>

        {hasPlan && !isPending && (
          <div className="flex flex-col gap-3">
            <label className="pl-3.5 text-sm font-[450] text-gray-700">
              <AppFormattedMessage id="SIGNATURE" />
            </label>
            <div className="flex flex-col gap-4 rounded-md border border-gray-50 p-4 shadow-sm">
              {docuSignHasIssue ? (
                <ErrorWrapper
                  actionButton={
                    remainingEnvelopes === 0 ? (
                      <button
                        className="text-xs font-[500] text-brand-600 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          eventsModalStore
                            .getState()
                            .openModal({ type: EventsModalType.GET_MORE_ENVELOPES });
                        }}
                        type="button"
                      >
                        <AppFormattedMessage id="GET_MORE_ENVELOPES" />
                      </button>
                    ) : !hasTemplatesForPlan ? (
                      <button
                        className="text-xs font-[500] text-brand-600 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToTemplatesAndOpenCreate();
                        }}
                        type="button"
                      >
                        <AppFormattedMessage id="CREATE_TEMPLATES" />
                      </button>
                    ) : undefined
                  }
                  errorMessages={errorMessages}
                  hasError={docuSignHasIssue}
                >
                  {docuSignOptionCard}
                </ErrorWrapper>
              ) : (
                docuSignOptionCard
              )}

              <OptionCard
                description={<AppFormattedMessage id="GRANT_WITHOUT_SIGNATURE_DESCRIPTION" />}
                icon={
                  <CheckCircleIcon
                    className="size-6"
                    iconColor={
                      signatureField.value === GrantSignatureOption.WITHOUT_SIGNATURE
                        ? '#2565C8'
                        : '#344054'
                    }
                  />
                }
                isSelected={signatureField.value === GrantSignatureOption.WITHOUT_SIGNATURE}
                onClick={handleAddWithoutSignature}
                title={<AppFormattedMessage id="ADD_WITHOUT_SIGNATURE" />}
              />
            </div>
          </div>
        )}
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700"
            disabled={Boolean(isGrantEditWizard && editWizardStep === 1)}
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!canProceed}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="NEXT" />
          </Button>
        </div>
      </div>
    </>
  );
};

export { GrantStepFirst };
